/**
 * One privacy-safe description of an audio block. It deliberately cannot recreate
 * speech: no samples, spectrum, pitch, or transcript leave the microphone. These are
 * the aggregate quantities needed to spot clipping, weak input, and a high noise floor.
 */
export interface AcousticFrame {
  durationMs: number
  rms: number
  peak: number
  clippingRatio: number
  zeroCrossingRate: number
}

/** Pure so recorded fixtures and the live microphone are measured identically. */
export function analyseAudioFrame(samples: Float32Array, sampleRate: number): AcousticFrame {
  if (!samples.length || !(sampleRate > 0)) {
    return { durationMs: 0, rms: 0, peak: 0, clippingRatio: 0, zeroCrossingRate: 0 }
  }
  let squares = 0
  let peak = 0
  let clipped = 0
  let crossings = 0
  let previous = samples[0]
  for (let i = 0; i < samples.length; i++) {
    const value = samples[i]
    const magnitude = Math.abs(value)
    squares += value * value
    peak = Math.max(peak, magnitude)
    if (magnitude >= 0.999) clipped++
    if (i > 0 && ((previous < 0 && value >= 0) || (previous >= 0 && value < 0))) crossings++
    previous = value
  }
  return {
    durationMs: (samples.length / sampleRate) * 1000,
    rms: Math.sqrt(squares / samples.length),
    peak,
    clippingRatio: clipped / samples.length,
    zeroCrossingRate: crossings / Math.max(1, samples.length - 1),
  }
}
